import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    Alert,
    Snackbar,
    Chip,
    TextField,
    Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useAuth } from "../../../contexts/AuthContext";
import EmployeePicker from "../../pickers/EmployeePicker";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import ScheduleItemsList from "./ScheduleItemsList";
import ScheduleItemFormDialog from "./ScheduleItemFormDialog";
import DeleteScheduleItemDialog from "./DeleteScheduleItemDialog";
import EngineeringService from "../../../services/Engineering.service";

export default function EngineeringSchedule() {
    const { user } = useAuth();
    const [engineer, setEngineer] = useState(
        user
            ? {
                  id: user.id,
                  full_name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username,
                  username: user.username,
              }
            : null,
    );
    const [project, setProject] = useState(null);
    const [noProjectOnly, setNoProjectOnly] = useState(false);
    const [quote, setQuote] = useState(null);
    const [dueFrom, setDueFrom] = useState("");
    const [dueTo, setDueTo] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formDialog, setFormDialog] = useState({ open: false, item: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    // Completed/All history can grow to thousands of records over time, so
    // (unlike Active) we refuse to fetch an unscoped list for those views.
    const isScoped = Boolean(engineer) || Boolean(project) || noProjectOnly;
    const needsScope = statusFilter !== "active" && !isScoped;

    const refresh = useCallback(async () => {
        if (needsScope) {
            setItems([]);
            setError("");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const params = {};
            if (engineer) params.assigned_to = engineer.id;
            if (noProjectOnly) {
                params.project = "none";
                if (quote) params.quote = quote.id;
                if (dueFrom) params.due_after = dueFrom;
                if (dueTo) params.due_before = dueTo;
            } else if (project) {
                params.project = project.id;
            }
            const res = await EngineeringService.listScheduleItems(params);
            let results = Array.isArray(res.data) ? res.data : [];
            if (statusFilter === "active") {
                results = results.filter((i) => i.status !== "complete");
            } else if (statusFilter === "complete") {
                results = results.filter((i) => i.status === "complete");
            }
            setItems(results);
        } catch {
            setError("Failed to load the Engineering Schedule.");
        } finally {
            setLoading(false);
        }
    }, [engineer, project, noProjectOnly, quote, dueFrom, dueTo, statusFilter, needsScope]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleSaved = () => {
        refresh();
        showSnackbar(formDialog.item ? "Schedule item updated." : "Schedule item created.");
    };

    const handleDeleted = () => {
        refresh();
        showSnackbar("Schedule item deleted.", "info");
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <EmployeePicker
                            value={engineer}
                            onChange={setEngineer}
                            label="Engineer"
                            size="small"
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <ProjectPicker
                            value={project}
                            onChange={(val) => {
                                setProject(val);
                                if (val) setNoProjectOnly(false);
                            }}
                            label="Project"
                            size="small"
                            disabled={noProjectOnly}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setFormDialog({ open: true, item: null })}
                        sx={{ flexShrink: 0, height: 40 }}
                    >
                        New Item
                    </Button>
                </Box>
                <Box>
                    <Chip
                        label="No Project"
                        size="small"
                        color={noProjectOnly ? "primary" : "default"}
                        variant={noProjectOnly ? "filled" : "outlined"}
                        onClick={() => {
                            const next = !noProjectOnly;
                            setNoProjectOnly(next);
                            if (next) {
                                setProject(null);
                            } else {
                                setQuote(null);
                                setDueFrom("");
                                setDueTo("");
                            }
                        }}
                    />
                </Box>
                {noProjectOnly && (
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <QuotePicker
                                value={quote}
                                onChange={setQuote}
                                label="Quote"
                                size="small"
                            />
                        </Box>
                        <TextField
                            label="Due After"
                            type="date"
                            value={dueFrom}
                            onChange={(e) => setDueFrom(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Due Before"
                            type="date"
                            value={dueTo}
                            onChange={(e) => setDueTo(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                )}
                <ToggleButtonGroup
                    value={statusFilter}
                    exclusive
                    onChange={(_, val) => val && setStatusFilter(val)}
                    size="small"
                    fullWidth
                >
                    <ToggleButton value="active" sx={{ flex: 1 }}>
                        Active
                    </ToggleButton>
                    <ToggleButton value="complete" sx={{ flex: 1 }}>
                        Complete
                    </ToggleButton>
                    <ToggleButton value="all" sx={{ flex: 1 }}>
                        All
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {needsScope ? (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Typography variant="body2">
                        Select an engineer or project (or use "No Project") to view
                        {statusFilter === "complete" ? " completed" : " all"} items —
                        this list can grow too large to show unfiltered.
                    </Typography>
                </Box>
            ) : (
                <ScheduleItemsList
                    items={items}
                    loading={loading}
                    onEdit={(item) => setFormDialog({ open: true, item })}
                    onDelete={(item) => setDeleteDialog({ open: true, item })}
                />
            )}

            <ScheduleItemFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, item: null })}
                item={formDialog.item}
                onSaved={handleSaved}
            />

            <DeleteScheduleItemDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                item={deleteDialog.item}
                onDeleted={handleDeleted}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
